/**
 * @typedef {Object} PricingContext
 * @property {number|string|null} finalRate - Manual override rate input by user (highest priority).
 * @property {boolean} isBaseRate - True if the selected rate plan is the base rate (e.g., type === 'Base').
 * @property {boolean} isDefault - Flag indicating if the standard base rate configuration is default.
 * @property {number|null} roomRateAmount - Specific base rate amount mapped to the individual room.
 * @property {number|null} roomTypeAmount - Fallback rate amount mapped to the room's category type.
 * @property {number|null} customRatePlanAmount - Rate amount associated with any selected custom rate plan.
 */

/**
 * Calculates the final room price based on a strict priority hierarchy.
 * This utility is completely decoupled from UI state, ensuring it can be imported 
 * and used interchangeably in both Single and Group Reservation modules.
 *
 * @param {PricingContext} context - The context object containing all possible pricing variables.
 * @returns {number} The definitively calculated final price (always falls back to 0 to prevent NaN calculations).
 */
export function calculateFinalRoomPrice(context) {
    const {
        finalRate,
        isBaseRate,
        isDefault,
        roomRateAmount,
        roomTypeAmount,
        customRatePlanAmount
    } = context;

    // --- RULE 1: Manual Override (Highest Priority) ---
    // If the user manually inputs an amount, it strictly ignores all other rules.
    if (finalRate !== undefined && finalRate !== null && finalRate !== '') {
        const parsedOverride = parseFloat(finalRate);
        if (!isNaN(parsedOverride)) {
            return parsedOverride;
        }
    }

    const parseAmt = (val) => {
        if (val === undefined || val === null || val === '') return NaN;
        const num = Number(val);
        return isNaN(num) ? NaN : num;
    };

    const parsedRoomRate = parseAmt(roomRateAmount);
    const parsedRoomTypeRate = parseAmt(roomTypeAmount);
    const parsedCustomRate = parseAmt(customRatePlanAmount);

    // --- RULE 2: Standard Base Rate Selection ---
    if (isBaseRate) {
        if (isDefault) {
            // Attempt 2A: Try specific room rate first
            if (!isNaN(parsedRoomRate)) {
                return parsedRoomRate;
            }
            
            // Attempt 2B: Fallback to the room's overarching type amount
            if (!isNaN(parsedRoomTypeRate)) {
                return parsedRoomTypeRate;
            }
            
            return 0; // Ultimate fallback if both are missing
        } else {
            // Note: isDefault is false, so we handle it gracefully here
            // Returning customRatePlanAmount as a safe standard fallback, or 0
            return !isNaN(parsedCustomRate) ? parsedCustomRate : 0;
        }
    }

    // --- RULE 3: Custom Rate Plan Selection ---
    // If we reach here, a specific custom rate plan was selected instead of the base rate
    if (!isNaN(parsedCustomRate)) {
        return parsedCustomRate;
    }

    // Ultimate safeguard fallback
    return 0;
}

/**
 * @typedef {Object} RoomBillingItem
 * @property {string|number} [roomId] - Unique ID of the room or folio item
 * @property {string} [roomNumber] - Room number / identifier
 * @property {number} baseRate - Original pre-tax Base Rate amount
 * @property {number} taxRate - Tax percentage (e.g., 18 for 18% or 0.18)
 * @property {number} [taxAmount] - Optional pre-computed tax amount
 * @property {number} [grossTotal] - Optional pre-computed gross total (Base + Tax)
 */

/**
 * 1. Rate Override Weighting (Pre-Tax Target)
 * Formula: Room Base Rate / Total Reservation Base Rate
 * Why: A rate override fundamentally changes the core price of the room before taxes even exist in the calculation.
 * The weight must solely reflect the base value of the inventory.
 *
 * @param {RoomBillingItem[]} rooms - List of room items
 * @param {number} targetTotalBaseRate - The new target pre-tax base rate for the whole reservation
 * @returns {{
 *   rooms: Array<{
 *     roomId?: string|number,
 *     roomNumber?: string,
 *     originalBaseRate: number,
 *     taxRatePercent: number,
 *     baseShareRatio: number,
 *     newBaseRate: number,
 *     newTaxAmount: number,
 *     newGrossTotal: number
 *   }>,
 *   totals: {
 *     originalBaseTotal: number,
 *     newBaseTotal: number,
 *     newTaxTotal: number,
 *     newGrossTotal: number
 *   }
 * }}
 */
export function calculateRateOverrideAllocation(rooms, targetTotalBaseRate) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return {
            rooms: [],
            totals: {
                originalBaseTotal: 0,
                newBaseTotal: 0,
                newTaxTotal: 0,
                newGrossTotal: 0
            }
        };
    }

    const targetBaseNum = Math.max(0, Number(targetTotalBaseRate) || 0);
    const totalOriginalBase = rooms.reduce((sum, r) => sum + (Number(r.baseRate) || 0), 0);

    let allocatedBaseSum = 0;
    const allocatedRooms = rooms.map((room, index) => {
        const origBase = Number(room.baseRate) || 0;
        let tRate = Number(room.taxRate) || 0;
        if (tRate > 0 && tRate < 1) {
            tRate = tRate * 100;
        }
        const taxMultiplier = tRate / 100;

        // Weight = Room Base Rate / Total Reservation Base Rate
        const baseShareRatio = totalOriginalBase > 0 ? (origBase / totalOriginalBase) : (1 / rooms.length);

        let newBaseRate = 0;
        if (index === rooms.length - 1) {
            newBaseRate = Number((targetBaseNum - allocatedBaseSum).toFixed(2));
        } else {
            newBaseRate = Number((targetBaseNum * baseShareRatio).toFixed(2));
            allocatedBaseSum += newBaseRate;
        }

        const newTaxAmount = Number((newBaseRate * taxMultiplier).toFixed(2));
        const newGrossTotal = Number((newBaseRate + newTaxAmount).toFixed(2));

        return {
            roomId: room.roomId,
            roomNumber: room.roomNumber,
            originalBaseRate: origBase,
            taxRatePercent: tRate,
            baseShareRatio,
            newBaseRate,
            newTaxAmount,
            newGrossTotal
        };
    });

    const originalBaseTotal = Number(totalOriginalBase.toFixed(2));
    const newBaseTotal = Number(allocatedRooms.reduce((s, r) => s + r.newBaseRate, 0).toFixed(2));
    const newTaxTotal = Number(allocatedRooms.reduce((s, r) => s + r.newTaxAmount, 0).toFixed(2));
    const newGrossTotal = Number(allocatedRooms.reduce((s, r) => s + r.newGrossTotal, 0).toFixed(2));

    return {
        rooms: allocatedRooms,
        totals: {
            originalBaseTotal,
            newBaseTotal,
            newTaxTotal,
            newGrossTotal
        }
    };
}

/**
 * @typedef {Object} DiscountAllocationResult
 * @property {string|number} [roomId]
 * @property {string} [roomNumber]
 * @property {number} originalBaseRate
 * @property {number} taxRatePercent - e.g. 18
 * @property {number} originalTaxAmount
 * @property {number} originalGrossTotal
 * @property {number} grossShareRatio - e.g. 0.692082 (69.21%)
 * @property {number} grossDiscountShare - e.g. 1384.16
 * @property {number} netBaseRevenueHit - e.g. 1173.02
 * @property {number} taxReversed - e.g. 211.14
 * @property {number} newNetBaseRate - e.g. 8826.98
 * @property {number} newTaxAmount - e.g. 1588.86
 * @property {number} finalRoomTotal - e.g. 10415.84
 */

/**
 * 2. Flat Checkout Discount Weighting (Post-Tax Target)
 * Formula: Room Gross Total / Master Reservation Gross Total (Where Gross = Base + Tax)
 * Why: A checkout discount reduces the final out-of-pocket cost for the guest. Since the guest's final bill includes tax,
 * the weighting engine must calculate the proportions based on those fully taxed totals.
 *
 * Step 1: Computes Gross Total (Base + Tax) per room and Master Gross Total.
 * Step 2: Calculates each room's Gross Share Ratio (Total / Master).
 * Step 3: Proportional Allocation of the flat discount based on Gross Share Ratio.
 * Step 4: Reverse-extracts Net Base Revenue Hit & Tax Reversed per room's tax slab.
 *
 * @param {RoomBillingItem[]} rooms - List of room billing items
 * @param {number} flatDiscount - The overall flat discount applied at checkout
 * @returns {{
 *   rooms: DiscountAllocationResult[],
 *   totals: {
 *     originalBaseTotal: number,
 *     netBaseRevenueHitTotal: number,
 *     newNetBaseTotal: number,
 *     originalTaxTotal: number,
 *     taxReversedTotal: number,
 *     newTaxTotal: number,
 *     originalGrossTotal: number,
 *     flatDiscountTotal: number,
 *     finalGrandTotal: number
 *   }
 * }}
 */
export function calculateFlatCheckoutDiscount(rooms, flatDiscount) {
    if (!Array.isArray(rooms) || rooms.length === 0) {
        return {
            rooms: [],
            totals: {
                originalBaseTotal: 0,
                netBaseRevenueHitTotal: 0,
                newNetBaseTotal: 0,
                originalTaxTotal: 0,
                taxReversedTotal: 0,
                newTaxTotal: 0,
                originalGrossTotal: 0,
                flatDiscountTotal: 0,
                finalGrandTotal: 0
            }
        };
    }

    const discountNum = Math.max(0, Number(flatDiscount) || 0);

    // Prepare room base, tax rate and gross amounts
    const preparedRooms = rooms.map((room) => {
        const base = Number(room.baseRate) || 0;
        let tRate = Number(room.taxRate) || 0;
        // Normalize tax rate: if passed as e.g. 0.18, convert to 18, or if 18 keep as 18
        if (tRate > 0 && tRate < 1) {
            tRate = tRate * 100;
        }
        const taxMultiplier = tRate / 100;
        const taxAmt = room.taxAmount !== undefined && room.taxAmount !== null
            ? Number(room.taxAmount)
            : Number((base * taxMultiplier).toFixed(2));
        const gross = room.grossTotal !== undefined && room.grossTotal !== null
            ? Number(room.grossTotal)
            : Number((base + taxAmt).toFixed(2));

        return {
            ...room,
            base,
            taxRatePercent: tRate,
            taxMultiplier,
            taxAmount: taxAmt,
            grossTotal: gross
        };
    });

    const masterGrossTotal = preparedRooms.reduce((sum, r) => sum + r.grossTotal, 0);

    let allocatedDiscountSum = 0;
    const allocatedRooms = preparedRooms.map((r, index) => {
        const grossShareRatio = masterGrossTotal > 0 ? (r.grossTotal / masterGrossTotal) : (1 / preparedRooms.length);
        
        let grossDiscountShare = 0;
        if (index === preparedRooms.length - 1) {
            // Last item absorbs any rounding difference to ensure exact penny match with flatDiscount
            grossDiscountShare = Number((discountNum - allocatedDiscountSum).toFixed(2));
        } else {
            grossDiscountShare = Number((discountNum * grossShareRatio).toFixed(2));
            allocatedDiscountSum += grossDiscountShare;
        }

        // Reverse extraction
        // Net Base Revenue Hit: GrossDiscountShare / (1 + TaxRate)
        const netBaseRevenueHit = Number((grossDiscountShare / (1 + r.taxMultiplier)).toFixed(2));
        const taxReversed = Number((grossDiscountShare - netBaseRevenueHit).toFixed(2));

        const newNetBaseRate = Number((r.base - netBaseRevenueHit).toFixed(2));
        const newTaxAmount = Number((r.taxAmount - taxReversed).toFixed(2));
        const finalRoomTotal = Number((r.grossTotal - grossDiscountShare).toFixed(2));

        return {
            roomId: r.roomId,
            roomNumber: r.roomNumber,
            originalBaseRate: r.base,
            taxRatePercent: r.taxRatePercent,
            originalTaxAmount: r.taxAmount,
            originalGrossTotal: r.grossTotal,
            grossShareRatio: grossShareRatio,
            grossDiscountShare,
            netBaseRevenueHit,
            taxReversed,
            newNetBaseRate,
            newTaxAmount,
            finalRoomTotal
        };
    });

    const originalBaseTotal = Number(allocatedRooms.reduce((s, r) => s + r.originalBaseRate, 0).toFixed(2));
    const netBaseRevenueHitTotal = Number(allocatedRooms.reduce((s, r) => s + r.netBaseRevenueHit, 0).toFixed(2));
    const newNetBaseTotal = Number(allocatedRooms.reduce((s, r) => s + r.newNetBaseRate, 0).toFixed(2));
    const originalTaxTotal = Number(allocatedRooms.reduce((s, r) => s + r.originalTaxAmount, 0).toFixed(2));
    const taxReversedTotal = Number(allocatedRooms.reduce((s, r) => s + r.taxReversed, 0).toFixed(2));
    const newTaxTotal = Number(allocatedRooms.reduce((s, r) => s + r.newTaxAmount, 0).toFixed(2));
    const originalGrossTotal = Number(allocatedRooms.reduce((s, r) => s + r.originalGrossTotal, 0).toFixed(2));
    const flatDiscountTotal = Number(allocatedRooms.reduce((s, r) => s + r.grossDiscountShare, 0).toFixed(2));
    const finalGrandTotal = Number(allocatedRooms.reduce((s, r) => s + r.finalRoomTotal, 0).toFixed(2));

    return {
        rooms: allocatedRooms,
        totals: {
            originalBaseTotal,
            netBaseRevenueHitTotal,
            newNetBaseTotal,
            originalTaxTotal,
            taxReversedTotal,
            newTaxTotal,
            originalGrossTotal,
            flatDiscountTotal,
            finalGrandTotal
        }
    };
}

